import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import OptimizedImage from '../optimized-image/optimized-image'
import { Translate } from '../../utils/translations'
import { campaignApi } from '../../campaigns/api'
import type { QuantityStepProps } from './quantity-step-props'

type Sd500QuantityFormValues = { sd500Qty?: number; emd500eQty?: number }

/**
 * Quantity step for the SD500 / EMD500E campaign.
 *
 * Collects the owned quantity for both products. Unlike the B900 crank step,
 * the rule here is "at least one product" (sum of both quantities must be >= 1).
 */
const Sd500QuantityForm: React.FC<QuantityStepProps> = ({
  onSuccess,
  requestor,
  language,
  submissionId,
  savedValues,
  onSaveValues,
}) => {
  const useCreateQuantity = campaignApi.useCreateQuantity
  const productQuantityMutation = useCreateQuantity({
    mutation: {
      onSuccess: data => {
        onSuccess(data.data)
      },
      onError: error => {
        console.error('error', error)
      },
    },
  })

  /**
   * Submits the collected quantities to the SD500/EMD500E product-quantity endpoint.
   */
  const handleSubmit = async (values: Sd500QuantityFormValues) => {
    if (!submissionId) {
      return
    }
    onSaveValues?.(values)
    await productQuantityMutation.mutateAsync({
      data: {
        submissionId,
        requestor: {
          country: requestor.country,
          email: requestor.email,
          language,
        },
        quantity: {
          sd500: Number(values.sd500Qty) || 0,
          emd500e: Number(values.emd500eQty) || 0,
        },
      },
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={
        {
          sd500Qty: savedValues?.sd500Qty,
          emd500eQty: savedValues?.emd500eQty,
        } as Sd500QuantityFormValues
      }
      validate={values => {
        const errors: any = {}
        const sd500 = Number(values.sd500Qty)
        const emd500e = Number(values.emd500eQty)

        // Each provided field must be a valid non-negative number
        if (values.sd500Qty !== undefined && (isNaN(sd500) || sd500 < 0)) {
          errors.sd500Qty = <Translate value="fields.required" />
        }
        if (values.emd500eQty !== undefined && (isNaN(emd500e) || emd500e < 0)) {
          errors.emd500eQty = <Translate value="fields.required" />
        }

        // At least one product is required
        if (!((sd500 || 0) + (emd500e || 0) >= 1)) {
          errors.sd500Qty = <Translate value="fields.required" />
          errors.emd500eQty = <Translate value="fields.required" />
        }

        return errors
      }}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          {/* Help block: shows where to read the model number on the scooter so
              the user can tell SD500E and MD500E apart before entering quantities. */}
          <Element mb={4} textAlign="center">
            <Element mb={2}>
              <Translate value="recall.modelNumberHelp" />
            </Element>
            <div style={{ maxWidth: 720, margin: 'auto' }}>
              <OptimizedImage
                imageName="sd500-em500e-model-number_2"
                alt="Emplacement de la référence modèle SD500E / MD500E"
              />
            </div>
          </Element>
          {/* Visual separator between the help image and the quantity selection. */}
          <hr />
          <Columns>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage imageName="sd500" alt="Produit SD500" priority />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionSd500" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="sd500Qty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="sd500Qty"
                  name="sd500Qty"
                  type="number"
                  min={0}
                  className="input"
                  placeholder="..."
                />
                {errors.sd500Qty && touched.sd500Qty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.sd500Qty}
                  </Element>
                )}
              </div>
            </Columns.Column>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage imageName="emd500e" alt="Produit EMD500E" priority />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionEmd500e" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="emd500eQty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="emd500eQty"
                  name="emd500eQty"
                  type="number"
                  min={0}
                  className="input"
                  placeholder="..."
                />
                {errors.emd500eQty && touched.emd500eQty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.emd500eQty}
                  </Element>
                )}
              </div>
            </Columns.Column>
          </Columns>
          <Element mt={4} textAlign="center">
            <Button
              color="primary"
              type="submit"
              disabled={isSubmitting}
              data-testid="step-submit"
            >
              <Translate value="form.submit" />
            </Button>
          </Element>
        </Form>
      )}
    </Formik>
  )
}

export default Sd500QuantityForm
